import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthentication, useUnsavedChanges } from '@connect-x-app/hybrid-state';
import { ConnectPermission, PermissionType, RolesPermissions } from '@connect-x-app/shared-domain-models';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Checkbox, IconWithTooltip, Spinner } from '@connect-x-app/web-ui';
import { isEqual } from 'lodash';

import { SaveButton } from '../../../../../shared';
import { usePermissions } from './hooks';
import { getAllPermissionsByType, getGroupedPermissionByType, PermissionGroupsType } from './permission-groups';
import { ActionTypes, reducer } from './reducer';

import './rolePermissions.scss';

export const RolePermissions = () => {
  const { t } = useTranslation();

  const { hasPermission } = useAuthentication();

  const { isLoading, permissions, updatePermissions } = usePermissions();

  const startPermissions = useRef(permissions);

  const [state, dispatch] = useReducer(reducer, permissions);

  const { setHasUnsavedChanges } = useUnsavedChanges();

  useEffect(() => {
    setHasUnsavedChanges(!isEqual(state, permissions));
  }, [permissions, setHasUnsavedChanges, state]);

  const areAllChecked = useCallback(
    (permissionType: PermissionType) => {
      const allPermissionsSet = new Set(getAllPermissionsByType(permissionType));
      return Array.from(allPermissionsSet).every(p => state.has(p));
    },
    [state],
  );

  const canUpdate = hasPermission(RolesPermissions.UPDATE);

  const saveDisabled = !canUpdate || isEqual(state, startPermissions.current);

  const onChangeGroup = (checked: boolean, permissions: ConnectPermission[]) => {
    dispatch({
      payload: permissions,
      type: checked ? ActionTypes.ADD : ActionTypes.REMOVE,
    });
  };

  const onChangeSingle = (checked: boolean, permission: string) => {
    dispatch({
      payload: [permission as ConnectPermission],
      type: checked ? ActionTypes.ADD : ActionTypes.REMOVE,
    });
  };

  const onSavePermissions = () => {
    updatePermissions(state);
    startPermissions.current = state;
  };

  const renderPermissionGroup = ({ key, permissions }: PermissionGroupsType) => {
    return (
      <tr className="permission-group" key={key}>
        <td className={`permission-group--title ${!canUpdate ? 'permission-disabled' : null}`}>
          <div className="permission-group--title-content">
            <Checkbox
              checked={permissions.map(({ permission }) => permission).every(permission => state.has(permission))}
              className="permission-wrapper permission-wrapper--body"
              disabled={!canUpdate}
              label={t(`ROLES.PERMISSIONS.KEYS.${key}`)}
              name={t(`ROLES.PERMISSIONS.KEYS.${key}`)}
              onChange={checked =>
                onChangeGroup(
                  checked,
                  permissions.map(({ permission }) => permission),
                )
              }
              testId={ADMIN_TEST_IDS.roles.permissionCheckbox}
            />
            {key === 'ROLES' ? (
              <IconWithTooltip
                iconName="circularInfoOutline"
                label={t('ROLES.PERMISSIONS.TABLE.INFORMATION_TOOLTIP.ROLES')}
                size={2}
              />
            ) : null}
          </div>
        </td>
        <td className="permission-group--section">
          {permissions.map(({ action, permission }) => (
            <div className={`permission-group--cell ${!canUpdate ? 'permission-disabled' : null}`} key={permission}>
              <Checkbox
                checked={state.has(permission)}
                className="permission-wrapper permission-wrapper--body"
                disabled={!hasPermission(RolesPermissions.UPDATE)}
                label={t(`ROLES.PERMISSIONS.ACTIONS.${action}`)}
                name={permission}
                onChange={onChangeSingle}
              />
            </div>
          ))}
        </td>
      </tr>
    );
  };

  const renderPermissionGroupTable = (permissionType: PermissionType, infoTooltipText?: string) => {
    const permissionGroups = getGroupedPermissionByType(permissionType);
    return (
      <>
        <div className="permission-type__container">
          <h3>{t(`ROLES.PERMISSIONS.TABLE.TITLE.${permissionType}`).toUpperCase()}</h3>
          {infoTooltipText ? <IconWithTooltip iconName="circularInfoOutline" label={infoTooltipText} size={2} /> : null}
        </div>
        <table className="permissions-table" data-test={ADMIN_TEST_IDS.roles.permissionsTable(permissionType)}>
          <thead className="permission-table--header">
            <tr className="permission-group">
              <th className={`permission-group--title ${!canUpdate ? 'permission-disabled' : null}`}>
                <Checkbox
                  checked={areAllChecked(permissionType)}
                  className="permission-wrapper permission-wrapper--title"
                  disabled={!canUpdate}
                  label={t('ROLES.PERMISSIONS.TABLE.HEADER.TITLE')}
                  name="select-all"
                  onChange={checked => onChangeGroup(checked, getAllPermissionsByType(permissionType))}
                />
              </th>
              <th className="permission-group--section">
                <div className="permission-wrapper permission-wrapper--title">
                  <span>{t('ROLES.PERMISSIONS.TABLE.HEADER.PERMISSIONS')}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="permission-table--body">{permissionGroups.map(renderPermissionGroup)}</tbody>
        </table>
      </>
    );
  };

  if (isLoading) {
    return <Spinner testId={ADMIN_TEST_IDS.roles.permissionsSpinner} />;
  }

  return (
    <main className="permissions-table-container">
      <div className="permissions-header">
        <SaveButton isDisabled={saveDisabled} onClick={onSavePermissions} />
      </div>
      {renderPermissionGroupTable(PermissionType.FLIGHTROOM)}
      {renderPermissionGroupTable(PermissionType.ADMIN, t('ROLES.PERMISSIONS.TABLE.INFORMATION_TOOLTIP.ADMIN'))}
    </main>
  );
};
